import type { ModuleView } from '@agentic/contracts';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  lifecycleKind,
  SkeletonRows,
  statusTone,
  Table,
  useToast,
} from '@agentic/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { client } from '../../app/api.ts';
import { modulesQuery } from '../../app/queries.ts';
import { isOperator } from '../../app/roles.ts';
import { QueryError } from '../../app/route-states.tsx';
import { CARD, PAGE, PageHeader } from '../../app/shell.tsx';
import { useMe } from '../../app/use-me.ts';
import {
  ImpactDialog,
  PHASES,
  type Restart,
  RestartProgress,
  useRestartWatcher,
} from '../../features/modules/module-change.tsx';
import {
  ManifestDetails,
  moduleStateLabel,
  moduleSummary,
  moduleTypeLabel,
  providesSummary,
} from '../../features/modules/module-details.tsx';

export const Route = createFileRoute('/_app/modules')({ component: ModulesPage });

function ModulesPage() {
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery(modulesQuery);
  const [restart, setRestart] = useState<Restart | null>(null);
  const [review, setReview] = useState<ModuleView | null>(null);
  const onRestartChange = useCallback(
    (next: Partial<Restart>) => {
      setRestart((r) => (r ? { ...r, ...next } : r));
      if (next.phase === 'back') void qc.invalidateQueries();
    },
    [qc],
  );
  useRestartWatcher(restart, onRestartChange);
  const toggle = useMutation({
    mutationFn: (m: ModuleView) =>
      m.enabled
        ? client.modules.disable({ params: { name: m.name } })
        : client.modules.enable({ params: { name: m.name } }),
    onSuccess: (res) => {
      setReview(null);
      setRestart({
        phase: 'requested',
        module: res.name,
        enabled: res.enabled,
        startedAt: Date.now(),
        sawDown: false,
      });
      toast.push({
        title: `Change saved: ${res.name} ${res.enabled ? 'enabled' : 'disabled'}`,
        description: 'Restart requested',
        tone: 'warn',
      });
    },
  });
  const operator = isOperator(me);
  const restarting = !!restart && restart.phase !== 'back';

  return (
    <>
      <PageHeader
        title="Modules"
        description="Business packs add capabilities, agents and tools; core services run the platform. Turning a pack on or off saves the change and restarts the platform."
      />
      <div className={PAGE}>
        {restart && (
          <Banner
            tone={restart.phase === 'back' ? 'info' : 'warn'}
            title={`${restart.module}: ${PHASES.find((p) => p.key === restart.phase)?.label ?? restart.phase}`}
            action={
              restart.phase === 'back' ? (
                <Button size="sm" variant="ghost" onClick={() => setRestart(null)}>
                  Dismiss
                </Button>
              ) : undefined
            }
          >
            <RestartProgress restart={restart} modules={q.data?.items} />
          </Banner>
        )}
        {review && (
          <ImpactDialog
            module={review}
            all={q.data?.items ?? []}
            pending={toggle.isPending}
            onConfirm={() => {
              if (!toggle.isPending) toggle.mutate(review);
            }}
            onClose={() => setReview(null)}
          />
        )}
        <div className={`overflow-hidden ${CARD}`}>
          {q.isPending ? (
            <SkeletonRows label="Loading modules" />
          ) : q.isError ? (
            <div className="p-4">
              <QueryError error={q.error} onRetry={() => void q.refetch()} />
            </div>
          ) : q.data.items.length === 0 ? (
            <EmptyState title="No modules loaded" />
          ) : (
            <Table aria-label="Modules" data-testid="modules-table">
              <thead>
                <tr>
                  <th scope="col">Module</th>
                  <th scope="col">State</th>
                  <th scope="col">Provides</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              {MODULE_GROUPS.map((g) => {
                const rows = q.data.items
                  .filter((m) => g.types.includes(m.type))
                  .sort((a, b) => a.name.localeCompare(b.name));
                if (rows.length === 0) return null;
                return (
                  <tbody key={g.key}>
                    <tr>
                      <th
                        scope="colgroup"
                        colSpan={4}
                        className="bg-surface-2 text-[13px] font-semibold text-ink"
                      >
                        {g.title} ({rows.length})<span className="ml-2 font-normal text-ink-2">{g.hint}</span>
                      </th>
                    </tr>
                    {rows.map((m) => (
                      <ModuleRow
                        key={m.name}
                        module={m}
                        operator={operator}
                        busy={restarting || toggle.isPending}
                        onReview={() => setReview(m)}
                      />
                    ))}
                  </tbody>
                );
              })}
            </Table>
          )}
        </div>
      </div>
    </>
  );
}

const MODULE_GROUPS: { key: string; title: string; hint: string; types: ModuleView['type'][] }[] = [
  {
    key: 'packs',
    title: 'Business packs',
    hint: 'Domain capabilities an operator can turn on or off.',
    types: ['pack'],
  },
  {
    key: 'core',
    title: 'Core and system services',
    hint: 'Platform services. Core services cannot be turned off from this page.',
    types: ['core', 'system'],
  },
];

function ModuleRow({
  module: m,
  operator,
  busy,
  onReview,
}: {
  module: ModuleView;
  operator: boolean;
  busy: boolean;
  onReview: () => void;
}) {
  const summary = moduleSummary(m.manifest.metadata.description);
  return (
    <tr data-module={m.name}>
      <td className="min-w-[16rem] align-top">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-mono text-[13px] font-medium">{m.name}</span>
          <span className="text-[12px] text-ink-3">
            {moduleTypeLabel(m.type)} · v{m.version}
          </span>
        </div>
        {summary.text && <p className="mt-0.5 max-w-[34rem] text-[14px] text-ink-2">{summary.text}</p>}
        <ManifestDetails module={m} />
      </td>
      <td className="align-top">
        <Badge tone={statusTone(lifecycleKind(m.state))} dot title={m.state} data-state={m.state}>
          {moduleStateLabel(m.state)}
          <span className="sr-only"> ({m.state})</span>
        </Badge>
        <div className="mt-1 text-[12px] text-ink-3">{m.enabled ? 'Enabled' : 'Not enabled'}</div>
      </td>
      <td className="align-top text-[13px] text-ink-2">{providesSummary(m)}</td>
      <td className="text-right align-top">
        {operator && m.type !== 'core' && (
          <Button
            size="sm"
            variant={m.enabled ? 'secondary' : 'primary'}
            disabled={busy}
            aria-haspopup="dialog"
            onClick={onReview}
          >
            {m.enabled ? 'Disable' : 'Enable'}
          </Button>
        )}
      </td>
    </tr>
  );
}
